/**
 * Helper functions for notification policy compliance checking
 */

// Type definitions
export type NotificationType =
  | 'email'
  | 'slack'
  | 'microsoft_teams'
  | 'sms'
  | 'whats_app'
  | 'voice'
  | 'apns'
  | 'fcm';

export type PolicyNotificationGroupMethod =
  | 'email'
  | 'chat'
  | 'mobile_text'
  | 'voice'
  | 'push'
  | 'apns'
  | 'fcm'
  | 'any';

export type DestinationType =
  | 'email'
  | 'slack'
  | 'microsoft_teams'
  | 'phone'
  | 'apns'
  | 'fcm';

export type Priority = 'HIGH' | 'MEDIUM' | 'LOW';

export type Destination = {
  readonly type: DestinationType;
  readonly value: string;
};

export type PolicyItem = {
  readonly priority: Priority;
  readonly notification_group_method: PolicyNotificationGroupMethod;
  readonly max_delay: string;
};

export type NotificationPreference = {
  readonly id?: string;
  readonly notification_type:
    | NotificationType
    | { readonly notification_type: NotificationType };
  readonly notification_priority: Priority;
  readonly delay: string | { readonly value: string };
  readonly destination?: string;
};

export type ComplianceResult = {
  readonly isCompliant: boolean | null;
  readonly message: string;
  readonly requiredDelay?: string;
  readonly policyItem?: PolicyItem;
  readonly isTimeCompliant?: boolean;
};

export type ComplianceChanges = {
  readonly updates: ReadonlyArray<NotificationPreference>;
  readonly creates: ReadonlyArray<NotificationPreference>;
  readonly unsatisfiable: ReadonlyArray<{
    readonly policyItem: PolicyItem;
    readonly reason: string;
  }>;
};

// Map notification types from preferences to policy notification group methods
const NOTIFICATION_TYPE_MAP = {
  email: ['email'],
  slack: ['chat'],
  microsoft_teams: ['chat'],
  sms: ['mobile_text'],
  whats_app: ['mobile_text'],
  voice: ['voice'],
  apns: ['push'],
  fcm: ['push'],
} as const satisfies Record<
  NotificationType,
  ReadonlyArray<PolicyNotificationGroupMethod>
>;

// Reverse mapping from policy notification group methods to preference types
const POLICY_TO_PREFERENCE_MAP = {
  email: ['email'],
  chat: ['slack', 'microsoft_teams'],
  mobile_text: ['sms', 'whats_app'],
  voice: ['voice'],
  push: ['apns', 'fcm'],
  apns: ['apns'],
  fcm: ['fcm'],
  any: [
    'email',
    'slack',
    'microsoft_teams',
    'sms',
    'whats_app',
    'voice',
    'apns',
    'fcm',
  ],
} as const satisfies Record<
  PolicyNotificationGroupMethod,
  ReadonlyArray<NotificationType>
>;

/**
 * Maps policy notification group methods to available destination types
 */
const POLICY_TO_DESTINATION_TYPES = {
  email: ['email'],
  chat: ['slack', 'microsoft_teams'],
  mobile_text: ['phone'], // SMS via phone
  voice: ['phone'], // Voice calls via phone
  apns: ['apns'], // iOS push notifications
  fcm: ['fcm'], // Firebase Cloud Messaging (Android push notifications)
  push: ['apns', 'fcm'], // Generic push notifications - both iOS and Android
  any: ['email', 'slack', 'microsoft_teams', 'phone', 'apns', 'fcm'], // Any destination works, but prefer email
} as const satisfies Record<
  PolicyNotificationGroupMethod,
  ReadonlyArray<DestinationType>
>;

/**
 * Finds available destinations that can satisfy a policy notification group method
 */
export const findDestinationsForPolicy = (
  policyNotificationGroupMethod: PolicyNotificationGroupMethod,
  availableDestinations: ReadonlyArray<Destination>,
): ReadonlyArray<Destination> => {
  if (!policyNotificationGroupMethod || !availableDestinations) {
    return [];
  }

  const supportedDestinationTypes =
    POLICY_TO_DESTINATION_TYPES[policyNotificationGroupMethod] || [];

  const filteredDestinations = availableDestinations.filter((destination) =>
    (supportedDestinationTypes as ReadonlyArray<string>).includes(
      destination.type,
    ),
  );

  return filteredDestinations;
};

/**
 * Gets the preferred destination for a policy notification group method
 */
export const getPreferredDestinationForPolicy = (
  policyNotificationGroupMethod: PolicyNotificationGroupMethod,
  availableDestinations: ReadonlyArray<Destination>,
): Destination | null => {
  const matchingDestinations = findDestinationsForPolicy(
    policyNotificationGroupMethod,
    availableDestinations,
  );

  if (matchingDestinations.length === 0) {
    return null;
  }

  // For "any" policy, prefer email destinations first
  if (policyNotificationGroupMethod === 'any') {
    const emailDestinations = matchingDestinations.filter(
      (destination) => destination.type === 'email',
    );
    if (emailDestinations.length > 0) {
      return emailDestinations[0];
    }
  }

  // Return the first available destination
  // Could be enhanced with preference logic (e.g., prefer primary email, default phone, etc.)
  return matchingDestinations[0];
};

/**
 * Gets the notification type that should be used for a policy notification group method
 */
export const getNotificationTypeForPolicy = (
  policyNotificationGroupMethod: PolicyNotificationGroupMethod,
  destination: Destination | null = null,
): NotificationType => {
  // If a destination is provided, use its type for more accurate mapping
  if (destination) {
    const destinationTypeMap = {
      email: 'email',
      slack: 'slack',
      microsoft_teams: 'microsoft_teams',
      phone: policyNotificationGroupMethod === 'voice' ? 'voice' : 'sms',
      apns: 'apns',
      fcm: 'fcm',
    } as const satisfies Record<DestinationType, NotificationType>;

    if (destinationTypeMap[destination.type]) {
      return destinationTypeMap[destination.type];
    }
  }

  // Fallback to policy-based mapping
  const policyToNotificationTypeMap = {
    email: 'email',
    chat: 'slack', // Default to slack, but could be teams
    mobile_text: 'sms',
    voice: 'voice',
    apns: 'apns',
    fcm: 'fcm',
    push: 'apns', // Default to apns for generic push, but could be fcm
    any: 'email', // For "any" policy, default to email
  } as const satisfies Record<PolicyNotificationGroupMethod, NotificationType>;

  return policyToNotificationTypeMap[policyNotificationGroupMethod] || 'email';
};

/**
 * Convert ISO 8601 duration string to milliseconds
 */
export const convertToMilliseconds = (duration: string): number => {
  if (!duration) return 0;

  const matches = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!matches) return 0;

  const hours = parseInt(matches[1]) || 0;
  const minutes = parseInt(matches[2]) || 0;
  const seconds = parseInt(matches[3]) || 0;

  return hours * 60 * 60 * 1000 + minutes * 60 * 1000 + seconds * 1000;
};

/**
 * Format milliseconds to human-readable duration
 */
export const formatDelayFromMilliseconds = (milliseconds: number): string => {
  if (milliseconds === 0) return 'Immediately';

  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return hours === 1 ? '1 hour' : `${hours} hours`;
  } else if (minutes > 0) {
    return minutes === 1 ? '1 min' : `${minutes} mins`;
  } else {
    return seconds === 1 ? '1 sec' : `${seconds} secs`;
  }
};

/**
 * Format ISO 8601 duration to human-readable format
 */
export const formatDelay = (duration: string): string => {
  return formatDelayFromMilliseconds(convertToMilliseconds(duration));
};

/**
 * Check if a notification preference is compliant with applicable policies
 */
export const getRowComplianceStatus = (
  preference: NotificationPreference,
  policies: ReadonlyArray<PolicyItem>,
): ComplianceResult => {
  if (!preference || !policies || policies.length === 0) {
    return { isCompliant: null, message: 'No policy set' };
  }

  const preferenceType =
    typeof preference.notification_type === 'string'
      ? preference.notification_type
      : preference.notification_type.notification_type;
  const preferencePriority = preference.notification_priority;
  const preferenceDelay =
    typeof preference.delay === 'string'
      ? preference.delay
      : preference.delay.value;

  // Find all applicable policies that could be satisfied by this preference
  const applicablePolicies = policies.filter((policy) => {
    // Check priority match
    if (policy.priority !== preferencePriority) return false;

    // Check notification type match
    if (policy.notification_group_method === 'any') {
      return true; // 'any' matches all notification types
    }

    // Map preference type to policy group methods
    const preferenceGroupMethods = NOTIFICATION_TYPE_MAP[preferenceType] || [];
    return (preferenceGroupMethods as ReadonlyArray<string>).includes(
      policy.notification_group_method,
    );
  });

  if (applicablePolicies.length === 0) {
    return { isCompliant: null, message: 'No applicable policy' };
  }

  // Check if the preference is compliant with at least one applicable policy
  const preferenceDelayMs = convertToMilliseconds(preferenceDelay);

  const compliantPolicy = applicablePolicies.find((policy) => {
    const policyMaxDelayMs = convertToMilliseconds(policy.max_delay);
    return preferenceDelayMs <= policyMaxDelayMs;
  });

  if (compliantPolicy) {
    return {
      isCompliant: true,
      message: 'Compliant with policy',
      isTimeCompliant: true,
    };
  } else {
    // Find the most restrictive policy to show what's needed
    const mostRestrictivePolicy = applicablePolicies.reduce((min, policy) => {
      const policyDelayMs = convertToMilliseconds(policy.max_delay);
      const minDelayMs = convertToMilliseconds(min.max_delay);
      return policyDelayMs < minDelayMs ? policy : min;
    });

    return {
      isCompliant: false,
      requiredDelay: mostRestrictivePolicy.max_delay,
      message: `Per your organization's policy, this value should be set to: ${formatDelay(mostRestrictivePolicy.max_delay)}`,
      policyItem: mostRestrictivePolicy,
    };
  }
};

export const getGroupComplianceStatus = (
  preferences: ReadonlyArray<NotificationPreference>,
  policies: ReadonlyArray<PolicyItem>,
  notificationPriority: Priority,
): {
  groupIsTimeCompliant: boolean;
  priorityCompliance: Record<string, ComplianceResult>;
} => {
  const priorityCompliance = {};
  let groupIsTimeCompliant = false;
  preferences.forEach((item) => {
    priorityCompliance[item.id] = getRowComplianceStatus(
      {
        ...item,
        notification_priority: notificationPriority,
      },
      policies,
    );
    if (
      priorityCompliance[item.id].isCompliant &&
      priorityCompliance[item.id].isTimeCompliant
    ) {
      groupIsTimeCompliant = true;
    }
  });
  return { groupIsTimeCompliant, priorityCompliance };
};

/**
 * Check if a policy is satisfied by any existing user preference
 */
const isPolicySatisfied = (
  policy: PolicyItem,
  userPreferences: ReadonlyArray<NotificationPreference>,
): boolean => {
  const policyMaxDelayMs = convertToMilliseconds(policy.max_delay);
  const possiblePreferenceTypes =
    POLICY_TO_PREFERENCE_MAP[policy.notification_group_method] || [];

  return userPreferences.some((preference) => {
    const preferenceType =
      typeof preference.notification_type === 'string'
        ? preference.notification_type
        : preference.notification_type.notification_type;
    const preferencePriority = preference.notification_priority;
    const preferenceDelay =
      typeof preference.delay === 'string'
        ? preference.delay
        : preference.delay.value;
    const preferenceDelayMs = convertToMilliseconds(preferenceDelay);

    // Check if priority matches
    if (preferencePriority !== policy.priority) return false;

    // Check if notification type matches
    if (policy.notification_group_method === 'any') {
      // 'any' policy can be satisfied by any notification type
      return preferenceDelayMs <= policyMaxDelayMs;
    } else {
      // Specific notification type must match
      return (
        (possiblePreferenceTypes as ReadonlyArray<string>).includes(
          preferenceType,
        ) && preferenceDelayMs <= policyMaxDelayMs
      );
    }
  });
};

/**
 * Identify policies that are not being satisfied by any existing user preferences
 */
export const getUnmetPolicies = (
  policies: ReadonlyArray<PolicyItem>,
  userPreferences: ReadonlyArray<NotificationPreference>,
  priority: Priority,
): ReadonlyArray<PolicyItem> => {
  if (!policies || !userPreferences || !priority) return [];

  // Filter policies for the current priority
  const priorityPolicies = policies.filter(
    (policy) => policy.priority === priority,
  );

  // Find policies that are not satisfied by any existing preference
  return priorityPolicies.filter(
    (policy) => !isPolicySatisfied(policy, userPreferences),
  );
};

/**
 * Get notification type label for display
 */
export const getNotificationTypeLabel = (
  notificationType: NotificationType,
): string => {
  const typeMap = {
    email: 'Email',
    slack: 'Slack',
    microsoft_teams: 'Microsoft Teams',
    sms: 'SMS',
    whats_app: 'WhatsApp',
    voice: 'Voice',
    apns: 'iOS Push',
    fcm: 'Android Push',
  } as const satisfies Record<NotificationType, string>;
  return typeMap[notificationType] || notificationType;
};

/**
 * Get notification group method label for display
 */
export const getNotificationGroupMethodLabel = (
  notificationGroupMethod: PolicyNotificationGroupMethod,
): string => {
  const typeMap = {
    email: 'Email',
    chat: 'Slack or MS Teams',
    mobile_text: 'SMS or WhatsApp',
    voice: 'Voice',
    push: 'Push Notification',
    apns: 'iOS Push',
    fcm: 'Android Push',
    any: 'Any notification type',
  } as const satisfies Record<PolicyNotificationGroupMethod, string>;
  return typeMap[notificationGroupMethod] || notificationGroupMethod;
};

/**
 * Check if a preference exists for a given priority and notification type combination
 */
export const hasPreferenceForType = (
  currentPreferences: ReadonlyArray<NotificationPreference>,
  priority: Priority,
  notificationType: NotificationType,
): boolean => {
  return currentPreferences.some((pref) => {
    const prefType =
      typeof pref.notification_type === 'string'
        ? pref.notification_type
        : pref.notification_type.notification_type;
    return (
      pref.notification_priority === priority && prefType === notificationType
    );
  });
};

/**
 * Find the most restrictive policy requirements for each priority/notification type combination
 */
export const getMostRestrictivePolicyRequirements = (
  policies: ReadonlyArray<PolicyItem>,
): Record<string, PolicyItem> => {
  const policyRequirements: Record<string, PolicyItem> = {};

  policies.forEach((policy) => {
    const key = `${policy.priority}_${policy.notification_group_method}`;

    // If this is the first policy for this combination, or if this policy is more restrictive
    if (
      !policyRequirements[key] ||
      convertToMilliseconds(policy.max_delay) <
        convertToMilliseconds(policyRequirements[key].max_delay)
    ) {
      policyRequirements[key] = policy;
    }
  });

  return policyRequirements;
};

/**
 * Find an applicable policy requirement for a given preference
 */
export const findApplicableRequirement = (
  preference: NotificationPreference,
  policyRequirements: Record<string, PolicyItem>,
): PolicyItem | null => {
  const prefType =
    typeof preference.notification_type === 'string'
      ? preference.notification_type
      : preference.notification_type.notification_type;
  const prefPriority = preference.notification_priority;

  // Check for exact match first
  const exactKey = `${prefPriority}_${prefType}`;
  if (policyRequirements[exactKey]) {
    return policyRequirements[exactKey];
  }

  // Check for "any" policy that applies to this priority
  const anyKey = `${prefPriority}_any`;
  if (policyRequirements[anyKey]) {
    return policyRequirements[anyKey];
  }

  return null;
};

/**
 * Calculate what updates and creates are needed to make all preferences compliant
 */
export const calculateComplianceChanges = (
  currentPreferences: ReadonlyArray<NotificationPreference>,
  policies: ReadonlyArray<PolicyItem>,
  availableDestinations: ReadonlyArray<Destination>,
): ComplianceChanges => {
  const updates: NotificationPreference[] = [];
  const creates: NotificationPreference[] = [];
  const unsatisfiable: Array<{ policyItem: PolicyItem; reason: string }> = [];
  const policyRequirements = getMostRestrictivePolicyRequirements(policies);

  // Update existing preferences that are non-compliant
  currentPreferences.forEach((pref) => {
    const requirement = findApplicableRequirement(pref, policyRequirements);

    const prefDelay =
      typeof pref.delay === 'string' ? pref.delay : pref.delay.value;

    if (
      requirement &&
      convertToMilliseconds(prefDelay) >
        convertToMilliseconds(requirement.max_delay)
    ) {
      updates.push({
        ...pref,
        delay: requirement.max_delay,
      });
    }
  });

  // Create missing preferences for policies that don't have matching preferences
  Object.values(policyRequirements).forEach((requirement) => {
    // Skip "any" policies - they don't need specific preferences created
    if (requirement.notification_group_method === 'any') {
      // For "any" policies, check if there's at least one preference with the right priority
      const hasMatchingPref = currentPreferences.some((pref) => {
        const prefDelay =
          typeof pref.delay === 'string' ? pref.delay : pref.delay.value;
        return (
          pref.notification_priority === requirement.priority &&
          convertToMilliseconds(prefDelay) <=
            convertToMilliseconds(requirement.max_delay)
        );
      });

      // If no compliant preference exists, create one using the preferred destination
      if (!hasMatchingPref) {
        const preferredDestination = getPreferredDestinationForPolicy(
          'any',
          availableDestinations,
        );

        if (preferredDestination) {
          const notificationType = getNotificationTypeForPolicy(
            'any',
            preferredDestination,
          );

          creates.push({
            notification_type: notificationType,
            destination: preferredDestination.value,
            delay: requirement.max_delay,
            notification_priority: requirement.priority,
          });
        } else {
          unsatisfiable.push({
            policyItem: requirement,
            reason:
              'No suitable destinations available for any notification type',
          });
        }
      }
      return;
    }

    // For specific notification group methods, check if there's a matching preference
    const hasMatchingPref = currentPreferences.some((pref) => {
      const prefType =
        typeof pref.notification_type === 'string'
          ? pref.notification_type
          : pref.notification_type.notification_type;
      const prefPriority = pref.notification_priority;
      const prefDelay =
        typeof pref.delay === 'string' ? pref.delay : pref.delay.value;

      // Check if priority matches
      if (prefPriority !== requirement.priority) return false;

      // Check if notification type matches
      let typeMatches = false;
      if (prefType === requirement.notification_group_method) {
        typeMatches = true;
      } else {
        // Handle reverse mapping for existing preferences
        const prefGroupMethods = NOTIFICATION_TYPE_MAP[prefType] || [];
        typeMatches = (prefGroupMethods as ReadonlyArray<string>).includes(
          requirement.notification_group_method,
        );
      }

      // Check if delay is compliant (preference delay must be <= policy max delay)
      const prefDelayMs = convertToMilliseconds(prefDelay);
      const policyMaxDelayMs = convertToMilliseconds(requirement.max_delay);
      const delayCompliant = prefDelayMs <= policyMaxDelayMs;

      return typeMatches && delayCompliant;
    });

    if (!hasMatchingPref) {
      // Find a suitable destination for this policy
      const preferredDestination = getPreferredDestinationForPolicy(
        requirement.notification_group_method,
        availableDestinations,
      );

      if (preferredDestination) {
        const notificationType = getNotificationTypeForPolicy(
          requirement.notification_group_method,
          preferredDestination,
        );

        creates.push({
          notification_type: notificationType,
          destination: preferredDestination.value,
          delay: requirement.max_delay,
          notification_priority: requirement.priority,
        });
      } else {
        const methodLabel = getNotificationGroupMethodLabel(
          requirement.notification_group_method,
        );
        unsatisfiable.push({
          policyItem: requirement,
          reason: `No suitable destinations available for ${methodLabel.toLowerCase()}`,
        });
      }
    }
  });

  return { updates, creates, unsatisfiable };
};
